import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { FaLock, FaShieldAlt, FaEye, FaEyeSlash, FaCheck } from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Helper para obtener headers de autenticación
const getAuthHeaders = () => {
  const token = localStorage.getItem('deisycfit_token');
  if (token) {
    return { Authorization: `Bearer ${token}` };
  }
  return {};
};

export default function ChangePasswordModal({ isOpen, onClose, onSuccess, isRequired = false }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const validatePassword = (password) => {
    return password.length >= 6;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!newPassword || !confirmPassword) {
      toast.error('Por favor completa todos los campos');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    
    if (!validatePassword(newPassword)) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, { headers: getAuthHeaders() });
      
      toast.success('¡Contraseña actualizada exitosamente!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.response?.data?.detail || 'Error al cambiar la contraseña');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={isRequired ? undefined : onClose}>
      <DialogContent className="max-w-md" style={{ backgroundColor: '#111111', border: '1px solid rgba(255,255,255,0.1)' }}>
        <DialogHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full flex items-center justify-center mb-4">
            <FaShieldAlt className="text-white text-2xl" />
          </div>
          <DialogTitle className="text-xl font-bold text-white">
            {isRequired ? '🔐 Configura tu Contraseña' : '🔒 Cambiar Contraseña'}
          </DialogTitle>
          <DialogDescription className="text-gray-400 mt-2">
            {isRequired ? (
              <>
                ¡Bienvenid@ a la plataforma! 💪
                <br />
                <span className="text-pink-400 font-medium">
                  Por seguridad, configura una nueva contraseña personal.
                </span>
              </>
            ) : (
              'Ingresa tu contraseña actual y la nueva contraseña'
            )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          {!isRequired && (
            <div className="space-y-2">
              <Label htmlFor="current-password" className="text-gray-200">Contraseña Actual</Label>
              <div className="relative">
                <Input
                  id="current-password"
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Tu contraseña actual"
                  className="pr-10"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                >
                  {showCurrentPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="new-password" className="text-gray-200">Nueva Contraseña</Label>
            <div className="relative">
              <Input
                id="new-password"
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Mínimo 6 caracteres"
                className="pr-10"
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200"
                onClick={() => setShowNewPassword(!showNewPassword)}
              >
                {showNewPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            {newPassword && (
              <p className={`text-xs ${validatePassword(newPassword) ? 'text-green-400' : 'text-red-400'}`}>
                {validatePassword(newPassword) ? (
                  <><FaCheck className="inline mr-1" /> Contraseña válida</>
                ) : (
                  'La contraseña debe tener al menos 6 caracteres'
                )}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm-password" className="text-gray-200">Confirmar Nueva Contraseña</Label>
            <Input
              id="confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Repite tu nueva contraseña"
            />
            {confirmPassword && newPassword !== confirmPassword && (
              <p className="text-xs text-red-400">Las contraseñas no coinciden</p>
            )}
            {confirmPassword && newPassword === confirmPassword && (
              <p className="text-xs text-green-400">
                <FaCheck className="inline mr-1" /> Las contraseñas coinciden
              </p>
            )}
          </div>

          {isRequired && (
            <div className="rounded-lg p-3 text-sm" style={{ backgroundColor: 'rgba(234, 179, 8, 0.15)', border: '1px solid rgba(234, 179, 8, 0.3)' }}>
              <span className="text-yellow-400"><strong>💡 Consejo:</strong> Usa una contraseña que puedas recordar fácilmente pero que sea difícil de adivinar.</span>
            </div>
          )}

          <DialogFooter className="flex flex-col gap-2 sm:flex-col pt-4">
            <Button
              type="submit"
              disabled={loading || !newPassword || !confirmPassword || newPassword !== confirmPassword}
              className="w-full text-white"
              style={{ background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))' }}
            >
              {loading ? (
                'Guardando...'
              ) : (
                <>
                  <FaLock className="mr-2" /> {isRequired ? 'Configurar Contraseña' : 'Cambiar Contraseña'}
                </>
              )}
            </Button>
            {!isRequired && (
              <Button type="button" variant="ghost" onClick={onClose} className="w-full text-gray-400 hover:text-white hover:bg-white/10">
                Cancelar
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
